import PropTypes from "prop-types";
import { NavLink, useLocation } from "react-router-dom";
import { useAppStore } from "@/store/useAppStore";
import { usePersonaStore, displayAssistantName } from "@/store/usePersonaStore";
import { cn } from "@/lib/utils";
import { isNavSectionActive, shouldIgnoreSectionNav } from "@/lib/sidebarNavigation";
import { CONTENT_ENABLED } from "@/lib/featureFlags";
import { Logo } from "@/components/common/Logo";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Inbox,
  Send,
  Activity,
  FileText,
  Users,
  BookOpen,
  Settings,
  ChevronsLeft,
  ChevronsRight,
  Sparkles,
  BarChart3,
} from "lucide-react";

const items = [
  { to: "/today",    label: "Today",    icon: LayoutDashboard, phase: 1 },
  { to: "/inbox",    label: "Inbox",    icon: Inbox, phase: 1, preserveState: true },
  { to: "/people",   label: "Contact",  icon: Users, phase: 1 },
  { to: "/outreach", label: "Outreach", icon: Send, phase: 1 },
  { to: "/signals",  label: "Signals",  icon: Activity, phase: 1 },
  { to: "/reports",  label: "Reports",  icon: BarChart3, phase: 1 },
  { to: "/content",  label: "Content",  icon: FileText, phase: 1, extendedOnly: true },
  { to: "/brain",    label: "Brain",    icon: BookOpen, phase: 1 },
];

const navItems = items.filter((it) => CONTENT_ENABLED || !it.extendedOnly);

const adminItems = [
  { to: "/settings", label: "Settings", icon: Settings, phase: 1 },
];

function NavItem({ to, label, icon: Icon, phase, collapsed, onNavigate, touchFriendly, preserveState }) {
  const { pathname } = useLocation();
  const isInbound = to === "/inbound";
  const isActive = isNavSectionActive(pathname, to);
  const inDev = phase > 1;

  let titleText;
  if (collapsed) {
    titleText = label + (inDev ? ` (Phase ${phase})` : "");
  }

  const handleClick = (event) => {
    if (shouldIgnoreSectionNav({ pathname, to, preserveState })) event.preventDefault();
    onNavigate?.();
  };

  return (
    <NavLink
      to={to}
      end={!isInbound}
      onClick={handleClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={cn(
        "group relative flex items-center gap-2.5 rounded-md text-sm font-medium transition-colors",
        collapsed ? "justify-center px-2 py-2" : "px-2.5 py-1.5",
        touchFriendly && !collapsed && "min-h-11 py-2.5",
        isActive
          ? "bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200"
          : "text-slate-600 hover:bg-white/70 hover:text-slate-900",
      )}
      title={titleText}
    >
      {isActive && !collapsed && (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-indigo-500" aria-hidden />
      )}
      <Icon size={16} strokeWidth={isActive ? 2 : 1.75} className={cn(isActive ? "text-indigo-600" : "text-slate-500 group-hover:text-slate-700")} />
      {!collapsed && (
        <>
          <span className="truncate">{label}</span>
          {inDev && (
            <span className="ml-auto inline-flex items-center text-[10px] font-medium text-slate-400 group-hover:text-slate-500">
              P{phase}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

NavItem.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  phase: PropTypes.number.isRequired,
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
  touchFriendly: PropTypes.bool,
  preserveState: PropTypes.bool,
};

function assistantInitials(name) {
  const clean = displayAssistantName(name);
  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  return clean.slice(0, 2).toUpperCase() || "AI";
}

function SidebarNavBody({ collapsed, onNavigate, touchFriendly, showCollapseToggle }) {
  const { setSidebarCollapsed } = useAppStore();
  const assistantName = usePersonaStore((s) => s.name);
  const tagline = usePersonaStore((s) => s.tagline);
  const displayName = displayAssistantName(assistantName);
  const initials = assistantInitials(assistantName);
  const visibleNav = navItems;

  return (
    <>
      {!collapsed && (
        <div className="px-3 pt-3 pb-2">
          <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5">
            <span className="h-5 w-5 rounded-sm shrink-0 bg-gradient-to-br from-indigo-500 to-violet-600 text-white text-[10px] font-bold flex items-center justify-center">{initials}</span>
            <div className="min-w-0 leading-tight">
              <div className="text-[12px] font-medium text-slate-900 truncate" data-testid="sidebar-assistant-name">{displayName}</div>
              <div className="text-[10px] text-slate-500 truncate">{tagline || "Your AI SDR"} · online</div>
            </div>
            <span className="ml-auto h-1.5 w-1.5 rounded-full bg-emerald-500" />
          </div>
        </div>
      )}

      <nav className={cn("flex-1 overflow-y-auto scrollbar-thin", collapsed ? "px-2 py-2" : "px-3 py-1")}>
        <div className={cn("space-y-0.5", collapsed && "space-y-1", touchFriendly && "space-y-1")}>
          {visibleNav.map((it) => (
            <NavItem key={it.to} {...it} collapsed={collapsed} onNavigate={onNavigate} touchFriendly={touchFriendly} />
          ))}
        </div>
        <div className={cn("mt-4 pt-3 border-t border-slate-200/80 space-y-0.5", collapsed && "space-y-1", touchFriendly && "space-y-1")}>
          {!collapsed && (
            <div className="px-2.5 mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
              Instance
            </div>
          )}
          {adminItems.map((it) => (
            <NavItem key={it.to} {...it} collapsed={collapsed} onNavigate={onNavigate} touchFriendly={touchFriendly} />
          ))}
        </div>
      </nav>

      <div className={cn("border-t border-slate-200/80", collapsed ? "p-2" : "p-3")}>
        {!collapsed && (
          <div className="mb-2 rounded-md border border-indigo-200/70 bg-gradient-to-br from-indigo-50 to-white px-2.5 py-2">
            <div className="flex items-center gap-1.5 text-[11px] font-medium text-indigo-700">
              <Sparkles size={12} strokeWidth={2} />
              Powered by OpenClaw
            </div>
            <div className="mt-0.5 text-[10px] text-slate-500">Agent runtime · v3.2.1</div>
          </div>
        )}
        {showCollapseToggle && (
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!collapsed)}
            className={cn(
              "w-full inline-flex items-center justify-center gap-1.5 rounded-md text-[12px] text-slate-500 hover:bg-white hover:text-slate-700 ring-focus py-1.5",
            )}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <ChevronsRight size={14} /> : (
              <>
                <ChevronsLeft size={14} />
                <span>Collapse</span>
              </>
            )}
          </button>
        )}
      </div>
    </>
  );
}

SidebarNavBody.propTypes = {
  collapsed: PropTypes.bool,
  onNavigate: PropTypes.func,
  touchFriendly: PropTypes.bool,
  showCollapseToggle: PropTypes.bool,
};

/** Desktop sidebar — hidden below lg; collapse toggle unchanged at lg+. */
export function Sidebar() {
  const { sidebarCollapsed } = useAppStore();
  const collapsed = sidebarCollapsed;

  return (
    <aside
      className={cn(
        "hidden lg:flex shrink-0 border-r border-slate-200 bg-slate-50/80 backdrop-blur-sm flex-col transition-all duration-200",
        collapsed ? "w-[64px]" : "w-[232px]",
      )}
      data-testid="app-sidebar"
    >
      <div className={cn("h-14 flex items-center border-b border-slate-200/80 overflow-hidden", collapsed ? "justify-center px-1" : "px-3")}>
        {collapsed ? <Logo withText={false} size={28} /> : <Logo size={20} />}
      </div>
      <SidebarNavBody collapsed={collapsed} showCollapseToggle />
    </aside>
  );
}

/** Mobile/tablet nav drawer — used below lg via TopBar hamburger. */
export function MobileNavSheet({ open, onOpenChange }) {
  const close = () => onOpenChange(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="left"
        className="flex w-[min(100%,280px)] max-w-[280px] flex-col gap-0 p-0 bg-slate-50/95"
        data-testid="mobile-nav-sheet"
      >
        <SheetHeader className="h-14 shrink-0 flex-row items-center border-b border-slate-200/80 px-4 space-y-0 text-left">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <Logo size={20} />
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col">
          <SidebarNavBody collapsed={false} onNavigate={close} touchFriendly showCollapseToggle={false} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

MobileNavSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};
